import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { EnhancementMorphSection } from '@/components/EnhancementMorphSection';
import { Reveal } from '@/components/Reveal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Check,
  X,
  TrendingUp,
  Rocket,
  Crown,
  ArrowRight,
  Sparkles,
  Package,
  Sofa,
  Wand2,
  Video,
  Camera,
  ImagePlus,
  Building2,
  Loader2,
  RotateCcw,
  Star,
  ShieldCheck,
  Zap,
  Quote,
  CalendarCheck,
  Briefcase,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { SubscriptionTier } from '@/types/billing';
import { supabase } from '@/integrations/supabase/client';
import { getDataFastAttribution } from '@/lib/datafast';
import { useAuth } from '@/hooks/useAuth';
import { useBilling } from '@/hooks/useBilling';
import { useGeoCurrency } from '@/hooks/useGeoCurrency';
import { useLaunchPromo } from '@/hooks/useLaunchPromo';
import { toast } from 'sonner';
import { PageMeta } from '@/components/PageMeta';

// Testimonial portraits — reused from the homepage roster (real authored copy).
import portraitMarcus from '@/assets/testimonials/marcus.jpg';
import portraitEmma from '@/assets/testimonials/emma.jpg';
import portraitMichael from '@/assets/testimonials/michael.jpg';

// =============================================================================
// SHARED STYLE TOKENS
// =============================================================================

/** Premium dark-section background + a brighter on-dark accent color. */
const DARK_SECTION =
  'relative overflow-hidden bg-gradient-to-b from-[hsl(222_47%_12%)] via-[hsl(221_46%_9%)] to-[hsl(222_48%_7%)]';
const ACCENT = 'text-[hsl(205_100%_78%)]';

// =============================================================================
// PRICING
// =============================================================================

const PER_LISTING_PHOTO_ADDON = 5; // $5 per listing for 20 photos
const CINEMATIC_VIDEO_ADDON = 5; // $5 per listing for a Cinematic AI Video tour

const REEDIT_LIMITS: Record<SubscriptionTier, number> = {
  starter: 10,
  growth: 30,
  studio: 100,
  one_off_kit: 5,
  free: 2,
};

// =============================================================================
// VALUE STATS & SOCIAL PROOF
// =============================================================================

const valueStats = [
  { icon: Zap, metric: '~10 min', label: 'Per listing', sublabel: 'from photos to published' },
  { icon: TrendingUp, metric: '45%', label: 'More clicks', sublabel: 'on AI-enhanced photos' },
  { icon: Rocket, metric: '20%', label: 'Faster sales', sublabel: 'with sharper copywriting' },
  { icon: Sparkles, metric: '28', label: 'Languages', sublabel: 'one-click translations' },
];

const upgradeReasons = [
  {
    tier: 'Growth',
    icon: Rocket,
    headline: 'The sweet spot for active sellers',
    reasons: [
      'Premium descriptions written with conversion-tested keywords',
      'Shareable microsite — send buyers a link, not a PDF',
      'Custom watermark with your logo on every photo',
      'Up to 3 teammates collaborating on one workspace',
    ],
  },
  {
    tier: 'Team',
    icon: Crown,
    headline: 'Built for high-volume teams',
    reasons: [
      'AI Listing Score Card with actionable improvements',
      'Up to 50 active properties at any time',
      '10 seats for your full team',
      'API access to plug into your CRM or website',
    ],
  },
];

// Honest, already-claimed reassurances — placed right under the plans to de-risk.
const guarantees = [
  { icon: ShieldCheck, title: '14-day money-back', desc: 'Not impressed? Full refund — no questions asked.' },
  { icon: CalendarCheck, title: 'Cancel anytime', desc: "Keep everything you've already created." },
  { icon: Sparkles, title: 'No card to start', desc: 'Build your first listing free, then unlock.' },
  { icon: Zap, title: 'Live in ~10 minutes', desc: 'Raw photos to a published listing, fast.' },
];

// "The math" — typical à la carte cost of one polished listing vs ListBright.
const oldWayCosts = [
  { label: 'Professional photo shoot', cost: '$150–300' },
  { label: 'Photo editing & retouching', cost: '$50–100' },
  { label: 'Virtual staging', cost: '$30–75 / photo' },
  { label: 'Copywritten description', cost: '$80–150' },
  { label: 'Single-property microsite', cost: '$100+' },
];

// Agent-focused testimonials — exact copy reused from the homepage roster.
const testimonials = [
  {
    name: 'Marcus T.',
    role: 'Realtor',
    location: 'Austin, USA',
    photo: portraitMarcus,
    quote:
      'My listings used to sit for weeks. Now the photos pop on Zillow and I get showings within days — ListBright paid for itself on my first sale.',
  },
  {
    name: 'Emma W.',
    role: 'Estate Agent',
    location: 'Manchester, UK',
    photo: portraitEmma,
    quote:
      'Clients always comment on how professional our brochures look now. The microsite and QR code combo wins us instructions against far bigger agencies.',
  },
  {
    name: 'Michael L.',
    role: 'Broker',
    location: 'Sydney, Australia',
    photo: portraitMichael,
    quote:
      'I used to spend 30+ minutes writing each listing. Now it takes me 2 minutes and the quality is consistently better than anything I could write myself.',
  },
];

// =============================================================================
// PRICING PLANS
// =============================================================================

interface Plan {
  id: SubscriptionTier;
  name: string;
  description: string;
  icon: typeof TrendingUp;
  monthlyPriceUsd: number;
  yearlyPriceUsd: number;
  popular: boolean;
  listings: string;
  seats: number;
  /** Multiplier vs the entry plan, e.g. "3×". Shown next to the listing count. */
  multiplier?: string;
  includesHeading: string;
  includes: string[];
  /** Premium features this tier does NOT have — shown muted to drive upsell. */
  locked?: string[];
  cta: string;
  ctaVariant: 'default' | 'hero';
}

const plans: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'A few listings a month',
    icon: TrendingUp,
    monthlyPriceUsd: 30,
    yearlyPriceUsd: 300,
    popular: false,
    listings: '5',
    seats: 1,
    includesHeading: 'Everything to publish a listing:',
    includes: [
      'AI photo enhancement & cleanup',
      'Virtual staging + AI additions',
      'AI descriptions & social posts',
      'Free video slideshow',
      '28-language translations',
    ],
    locked: ['Shareable microsite', 'SEO-optimized copy', 'Custom watermark'],
    cta: 'Get Started',
    ctaVariant: 'default',
  },
  {
    id: 'growth',
    name: 'Growth',
    description: 'Active Realtors, agents & small teams',
    icon: Rocket,
    monthlyPriceUsd: 90,
    yearlyPriceUsd: 900,
    popular: true,
    listings: '15',
    seats: 3,
    multiplier: '3×',
    includesHeading: 'Everything in Starter, plus:',
    includes: [
      'Shareable listing microsite',
      'SEO-optimized, conversion-tested copy',
      'Custom watermark branding',
    ],
    cta: 'Get Started',
    ctaVariant: 'hero',
  },
  {
    id: 'studio',
    name: 'Team',
    description: 'Agencies & high-volume teams',
    icon: Crown,
    monthlyPriceUsd: 300,
    yearlyPriceUsd: 3000,
    popular: false,
    listings: '50',
    seats: 10,
    multiplier: '10×',
    includesHeading: 'Everything in Growth, plus:',
    includes: [
      'AI Listing Score Card',
      'API access',
      'Dedicated support',
    ],
    cta: 'Get Started',
    ctaVariant: 'default',
  },
];

const oneOffKit = {
  name: 'Single Property Kit',
  description: 'Perfect for selling a single property',
  icon: Package,
  priceUsd: 15,
  promoPriceUsd: 15,
  extraPhotosAddon: 5, // $5 per listing for 20 photos
  limits: {
    properties: '1',
    photos10: '10',
    photos20: '20',
  },
  features: [
    'All photo enhancements & staging included',
    '5 included re-edits for this property',
    'AI additions (pools, patios & more)',
    'Premium AI descriptions',
    'Shareable listing microsite & QR code',
    'PDF flyer & video slideshow',
    '28 language translations',
  ],
};

// =============================================================================
// WHAT EACH FEATURE DOES
// =============================================================================

const featureExplanations = [
  {
    id: 'ai-descriptions',
    name: 'AI Descriptions',
    icon: Sparkles,
    description: 'Compelling property descriptions written by AI',
    includes: [
      'Professional property descriptions',
      'SEO-optimized content',
      'Highlight key features automatically',
      'Customizable tone & style',
    ],
    pricing: 'Included with every property',
  },
  {
    id: 'social-media',
    name: 'Social Media Posts',
    icon: Camera,
    description: 'Ready-to-publish content for all major platforms',
    includes: [
      'Facebook Marketplace posts',
      'Instagram feed & story posts',
      'General marketplace listings',
      'Platform-optimized formatting',
    ],
    pricing: 'Included with every property',
  },
  {
    id: 'photo-editing',
    name: 'Photo Enhancement',
    icon: Camera,
    description: 'Professional AI enhancement for your property photos',
    includes: [
      'Brightness & exposure correction',
      'HDR & contrast optimization',
      'Color correction & saturation',
      'Object removal (clutter, wires, trash)',
      'Sky replacement (blue skies, sunset)',
      'Lawn & garden enhancement',
    ],
    pricing: 'Included with every property',
  },
  {
    id: 'virtual-staging',
    name: 'Virtual Staging',
    icon: Sofa,
    description: 'Add small decorative items to make spaces feel welcoming',
    includes: [
      'Small plants & potted greenery',
      'Decorative vases & art pieces',
      'Throw pillows & soft furnishings',
      'Applied to ALL photos in your property',
    ],
    note: 'We add tasteful decor — not furniture. The goal is to make empty spaces feel lived-in without misleading buyers.',
    pricing: 'Included with every property',
  },
  {
    id: 'ai-additions',
    name: 'AI Additions',
    icon: Wand2,
    description: 'Add features that don\'t exist yet',
    includes: [
      'Swimming pools',
      'Patios & outdoor living spaces',
      'Landscaping improvements',
      'Custom edits via text prompts',
    ],
    pricing: 'Included with every property',
  },
  {
    id: 'video',
    name: 'Video Slideshow',
    icon: Video,
    description: 'Create a professional property walkthrough video',
    includes: [
      'Smooth transitions between photos',
      'Background music',
      'Property details overlay',
      'Shareable video file',
    ],
    note: 'Want more? Upgrade any listing to a Cinematic AI Video tour for a one-time $5 — includes a free regeneration.',
    pricing: 'Included free with all plans',
  },
];

// =============================================================================
// FAQS
// =============================================================================

const faqs = [
  {
    question: 'Do I need to be a real estate agent to use ListBright?',
    answer: 'Not at all. Most of our users are homeowners, landlords, and holiday-rental owners who want a professional listing without hiring an agent. The Single Property Kit is built exactly for that. Real estate agents and agencies who list multiple properties a month use our subscription plans instead.',
  },
  {
    question: 'Is there a free trial? What\'s the catch?',
    answer: 'Yes — and no credit card required. You can build your first listing completely free and see exactly how good the AI-enhanced photos and description look. The preview is watermarked, so when you\'re ready to publish, you unlock the full-quality, watermark-free files. If you only have one property, the $15 Single Property Kit unlocks it; if you list regularly, a subscription unlocks it plus everything you create after.',
  },
  {
    question: 'What\'s the difference between the Kit and a subscription?',
    answer: 'The Single Property Kit is a one-time payment for one property — perfect if you\'re selling or renting your own home. Subscriptions ($30/mo and up) let you manage multiple active properties, add team members, use custom branding watermarks, and unlock the AI Listing Score Card. If you\'re not sure how many properties you\'ll list this year, start with the Kit.',
  },
  {
    question: 'Can I really list my own home without an agent?',
    answer: 'Yes — and thousands of owners do every month. The Kit gives you AI-enhanced photos, a compelling description in 28+ languages, a shareable microsite, a printable PDF flyer, a video slideshow, and ready-to-post social media content. You publish it on Facebook Marketplace, your favourite portal, or share the microsite link directly with buyers.',
  },
  {
    question: 'What does "Photo Enhancement" include?',
    answer: 'Photo Enhancement includes all AI enhancements: brightness correction, HDR, color optimization, object removal, sky replacement, and more. All enhancements are included with every property — no separate limits to worry about.',
  },
  {
    question: 'How does Virtual Staging work?',
    answer: 'Virtual Staging adds small decorative items (plants, vases, pillows) to make empty spaces feel welcoming. It\'s included with every property — all your photos get the treatment automatically.',
  },
  {
    question: 'What are AI Additions?',
    answer: 'AI Additions let you add features that don\'t exist yet: swimming pools, patios, landscaping, or any custom edit you describe. Included with every property.',
  },
  {
    question: 'What videos are included, and what is the Cinematic AI Video?',
    answer: 'Every listing includes a free video slideshow — built from your property photos with smooth transitions and music. If you want something more cinematic, you can upgrade any listing to a Cinematic AI Video tour (an AI-generated walkthrough) for a one-time $5 per-listing add-on, which includes one free regeneration.',
  },
  {
    question: 'What are AI Descriptions?',
    answer: 'AI Descriptions automatically generate professional, SEO-optimized property descriptions based on your photos and property details. The AI highlights key features and creates compelling copy that attracts buyers. Included free with every listing.',
  },
  {
    question: 'What Social Media Posts do I get?',
    answer: 'Every listing comes with ready-to-publish posts for Facebook Marketplace, Instagram feed, Instagram stories, and general marketplace platforms. Each post is formatted and optimized for the specific platform. Included free with every listing.',
  },
  {
    question: 'What is the shareable listing microsite?',
    answer: 'The shareable microsite is a professional preview page for your listing with a unique URL you can share with clients. It includes all photos, property details, and contact information. This feature is available exclusively on Growth and Team plans.',
  },
  {
    question: 'How many listings can I create?',
    answer: 'Your plan sets how many active listings you can have at once — Starter 5, Growth 15, Team 50, or a custom volume on Enterprise for larger agencies. An active listing is one with a live, shareable preview page. When a property sells, archive its listing to free up a slot for the next one, so you can create many listings over the life of your plan.',
  },
  {
    question: 'Can I add more photos per property?',
    answer: 'Yes! All plans include 10 photos per property by default. You can purchase a one-time $5 per-listing add-on to unlock 20 photos for a specific property. This is available on all plans including the Single Property Kit.',
  },
  {
    question: 'What is the Single Property Kit?',
    answer: 'A one-time purchase for selling a single property. Includes all Growth-level features: photo enhancement, virtual staging, AI additions, SEO optimized descriptions, shareable microsite, PDF flyer, video slideshow, 28 language translations, and 5 included re-edits. Comes with 10 photos — upgrade to 20 for an extra $5. Perfect for owners selling without an agent or subscription.',
  },
  {
    question: 'Do unused limits roll over?',
    answer: 'Monthly limits reset each billing cycle and don\'t roll over. If you need more capacity, consider upgrading to a higher plan.',
  },
  {
    question: 'Can I upgrade or downgrade anytime?',
    answer: 'Yes! Upgrades take effect immediately. Downgrades take effect at your next billing cycle. 14-day money-back guarantee on all plans.',
  },
];

// =============================================================================
// COMPARISON TABLE
// =============================================================================

const comparisonFeatures = [
  { name: 'Monthly Price', starter: '$30', growth: '$90', studio: '$300', isPrice: true },
  { name: 'Listings / month', starter: '5', growth: '15', studio: '50', isPrice: true },
  { name: 'Photos per listing', starter: '10', growth: '10', studio: '10' },
  { name: '20-photo add-on', starter: '$5/listing', growth: '$5/listing', studio: '$5/listing' },
  { name: 'Cinematic AI video add-on', starter: '$5/listing', growth: '$5/listing', studio: '$5/listing' },
  { name: 'Monthly Re-edits', starter: '10', growth: '30', studio: '100' },
  { name: 'Team Members', starter: '1', growth: '3', studio: '10' },
  { name: 'Photo Enhancement', starter: '✓', growth: '✓', studio: '✓' },
  { name: 'Virtual Staging', starter: '✓', growth: '✓', studio: '✓' },
  { name: 'AI Additions', starter: '✓', growth: '✓', studio: '✓' },
  { name: 'AI Descriptions', starter: '✓', growth: '✓', studio: '✓' },
  { name: 'Social Media Posts', starter: '✓', growth: '✓', studio: '✓' },
  { name: 'Free video slideshow', starter: '✓', growth: '✓', studio: '✓' },
  { name: '28 Languages', starter: '✓', growth: '✓', studio: '✓' },
  { name: 'Custom Watermark', starter: '—', growth: '✓', studio: '✓' },
  { name: 'SEO Optimized Description', starter: '—', growth: '✓', studio: '✓' },
  { name: 'Shareable Microsite', starter: '—', growth: '✓', studio: '✓' },
  { name: 'AI Listing Score Card', starter: '—', growth: '—', studio: '✓' },
  { name: 'API Access', starter: '—', growth: '—', studio: '✓' },
];

// Renders a comparison-table cell: ✓ → green check, — → muted dash, else text.
function CompareCell({ value }: { value: string }) {
  if (value === '✓') return <Check className="w-4 h-4 text-primary mx-auto" aria-label="Included" />;
  if (value === '—') return <span className="text-muted-foreground/40" aria-label="Not included">—</span>;
  return <>{value}</>;
}

// =============================================================================
// COMPONENT
// =============================================================================

export default function Pricing() {
  const [isYearly, setIsYearly] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { billing } = useBilling();
  const { currency } = useGeoCurrency();
  const checkoutCurrency = currency;
  const promo = useLaunchPromo();

  // Check if user has an active subscription (not one_off_kit)
  const isSubscribed = billing?.subscriptionStatus === 'active' &&
    billing?.subscriptionTier !== 'one_off_kit' &&
    billing?.subscriptionTier !== 'free';
  const currentTier = billing?.subscriptionTier;

  const formatPrice = (price: number) => {
    return `$${price}`;
  };

  const getDisplayPrice = (plan: typeof plans[0]) => {
    if (isYearly) {
      return Math.round(plan.yearlyPriceUsd / 12);
    }
    return plan.monthlyPriceUsd;
  };

  // Approximate cost per listing at the plan's listing cap — a powerful anchor
  // that makes the higher-volume plans read as better value.
  const getPerListing = (plan: Plan) => {
    const count = parseInt(plan.listings, 10);
    if (!count) return null;
    return Math.max(1, Math.round(getDisplayPrice(plan) / count));
  };

  const scrollToPlans = () => {
    document.getElementById('agent-plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handlePlanSelect = async (planId: SubscriptionTier) => {
    if (!user) {
      const params = new URLSearchParams({
        plan: planId,
        billing: isYearly ? 'yearly' : 'monthly',
      });
      navigate(`/auth?${params.toString()}`);
      return;
    }

    // Existing subscribers should manage their plan from Profile
    if (isSubscribed) {
      navigate('/profile?tab=plan');
      return;
    }

    setLoadingPlan(planId);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
      const locale = navigator.languages?.[0] || navigator.language || undefined;
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          planType: planId,
          photoLimit: 10,
          billingPeriod: isYearly ? 'yearly' : 'monthly',
          preferredCurrency: checkoutCurrency,
          timezone,
          locale,
          ...getDataFastAttribution(),
        },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleTryItKit = async () => {
    if (!user) {
      navigate('/auth?plan=try_it_kit');
      return;
    }

    setLoadingPlan('try_it_kit');
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
      const locale = navigator.languages?.[0] || navigator.language || undefined;
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          planType: 'try_it_kit',
          preferredCurrency: checkoutCurrency,
          timezone,
          locale,
          ...getDataFastAttribution(),
        },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };


  const faqJsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  }), []);

  return (
    <Layout>
      <PageMeta
        title="Pricing — AI Listing Suite from $30/mo | ListBright"
        description="Plans from $30/mo for the full suite: AI photos, virtual staging, descriptions, video, microsite and flyer. One-time kit also available."
        breadcrumbs={[{ label: 'Pricing', path: '/pricing' }]}
        jsonLd={faqJsonLd}
      />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-background via-background to-muted/30 py-16 sm:py-24">
        {/* Faint grid texture for depth */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div aria-hidden="true" className="absolute top-[-6rem] right-1/4 w-[28rem] h-[28rem] bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[-8rem] left-1/4 w-80 h-80 bg-accent/40 rounded-full blur-3xl pointer-events-none" />

        <div className="container max-w-3xl mx-auto px-4 relative">
          <div className="text-center space-y-6">
            <Badge variant="secondary" className="px-4 py-1.5 text-xs uppercase tracking-wider animate-fade-in">
              <Sparkles className="w-4 h-4 mr-2" />
              For Realtors, Agents & Brokers
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05] animate-fade-up">
              One subscription.{' '}
              <span className="text-gradient">Every listing, sold faster.</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed animate-fade-up" style={{ animationDelay: '80ms' }}>
              Every plan includes the full suite: photos, virtual staging, descriptions, video,
              microsite and flyer. Subscribe monthly, or pay once for a single property.
            </p>

            {/* Trust bar */}
            <div className="flex flex-col items-center gap-4 pt-1 animate-fade-up" style={{ animationDelay: '140ms' }}>
              <div className="flex items-center gap-2.5">
                <div className="flex">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <span className="text-sm font-medium text-foreground">
                  Loved by Realtors & owners in <span className="text-primary">15+ countries</span>
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-primary" /> Full toolkit included</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-primary" /> Cancel anytime</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-primary" /> 14-day money-back</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2 animate-fade-up" style={{ animationDelay: '200ms' }}>
              <Button size="lg" variant="hero" className="group shadow-lg min-h-[48px]" onClick={() => navigate('/auth')}>
                Start Free — No Card
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button size="lg" variant="outline" className="min-h-[48px]" onClick={scrollToPlans}>
                Compare Plans
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── ROI Stats Bar ── */}
      <section className="py-10 border-b border-border/50 bg-card/30">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {valueStats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <Reveal key={i} delay={i * 70}>
                  <div className="h-full text-center rounded-2xl border border-border/50 bg-card px-4 py-5 card-premium">
                    <div className="w-9 h-9 mx-auto rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                      <Icon className="w-[18px] h-[18px] text-primary" />
                    </div>
                    <div className="text-2xl sm:text-3xl font-display font-bold text-primary">{stat.metric}</div>
                    <div className="text-sm font-medium text-foreground">{stat.label}</div>
                    <div className="text-xs text-muted-foreground">{stat.sublabel}</div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Live AI Enhancement Showcase — high on the page to drive conversion */}
      <EnhancementMorphSection />

      {/* Realtor & agent plans — primary focus */}
      <section id="agent-plans" className="pt-16 pb-2 scroll-mt-24">
        <div className="container max-w-3xl mx-auto px-4 text-center">
          <Reveal>
            <Badge className="mb-4 bg-primary text-primary-foreground">For Realtors & Agents</Badge>
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-3">Pick your plan</h2>
            <p className="text-muted-foreground text-lg">
              Create more listings every month, collaborate with your team, and brand every photo with your logo —
              all from one subscription. Same full toolkit on every plan.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Pricing Cards ── */}
      <section className="py-10 sm:py-14">
        <div className="container max-w-6xl mx-auto px-4">
          {/* Segmented Billing Toggle */}
          <div className="flex flex-col items-center gap-2 mb-10">
            <div
              role="tablist"
              aria-label="Billing period"
              className="inline-flex items-center p-1 rounded-full border border-border/60 bg-card/80 backdrop-blur shadow-sm"
            >
              <button
                type="button"
                role="tab"
                aria-selected={!isYearly}
                onClick={() => setIsYearly(false)}
                className={`relative px-5 py-2 text-sm font-medium rounded-full transition-all ${
                  !isYearly
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isYearly}
                onClick={() => setIsYearly(true)}
                className={`relative px-5 py-2 text-sm font-medium rounded-full transition-all flex items-center gap-2 ${
                  isYearly
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Yearly
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                    isYearly
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  -2 mo
                </span>
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {isYearly ? 'You’re saving 2 months vs monthly billing' : 'Switch to yearly and get 2 months free'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-5 lg:gap-5 items-stretch">
            {plans.map((plan, planIndex) => {
              const Icon = plan.icon;
              const displayPrice = getDisplayPrice(plan);
              const formattedPrice = formatPrice(displayPrice);
              const isLoading = loadingPlan === plan.id;
              const perListing = getPerListing(plan);
              const seats = plan.seats;
              const isCurrent = currentTier === plan.id && isSubscribed;

              return (
                <Reveal key={plan.id} delay={planIndex * 90} className={`h-full ${plan.popular ? 'md:-mt-4' : ''}`}>
                  <div className="relative h-full">
                    {/* Halo glow behind the popular plan */}
                    {plan.popular && (
                      <div
                        aria-hidden="true"
                        className="absolute -inset-3 bg-gradient-to-b from-primary/25 via-primary/10 to-transparent blur-2xl rounded-[2rem] opacity-80 pointer-events-none"
                      />
                    )}
                    <Card
                      className={`relative flex flex-col h-full transition-all duration-300 ${
                        plan.popular
                          ? 'border-primary/60 ring-2 ring-primary/50 shadow-glow md:scale-[1.03] z-10'
                          : 'border-border/60 card-premium'
                      }`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                          <Badge className="gradient-gold text-primary-foreground border-0 px-3 py-1 shadow-elevated font-semibold">
                            <Star className="w-3.5 h-3.5 mr-1 fill-current" />
                            Most Popular
                          </Badge>
                        </div>
                      )}
                      {isCurrent && (
                        <div className="absolute -top-3 right-4 z-20">
                          <Badge className="bg-green-600 text-white border-green-700">Your Plan</Badge>
                        </div>
                      )}

                      <CardHeader className="text-center pb-4 pt-7">
                        <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-4 ${
                          plan.popular ? 'gradient-gold text-primary-foreground shadow-soft' : 'bg-primary/10'
                        }`}>
                          <Icon className={`w-6 h-6 ${plan.popular ? '' : 'text-primary'}`} />
                        </div>
                        <CardTitle className="text-xl">{plan.name}</CardTitle>
                        <CardDescription>{plan.description}</CardDescription>
                      </CardHeader>

                      <CardContent className="flex-1">
                        {(() => {
                          // Yearly view
                          if (isYearly && plan.monthlyPriceUsd > 0) {
                            const standardMonthly = Math.round(plan.yearlyPriceUsd / 12);
                            const monthsFree = Math.round((plan.monthlyPriceUsd * 12 - plan.yearlyPriceUsd) / plan.monthlyPriceUsd);
                            const monthsFreeLabel = monthsFree > 0 ? (monthsFree === 1 ? '1 month free' : `${monthsFree} months free`) : null;
                            return (
                              <div className="text-center mb-3">
                                <div className="text-base text-muted-foreground line-through">
                                  {formatPrice(plan.monthlyPriceUsd)}/month
                                </div>
                                <div className="mt-1">
                                  <span className="text-5xl font-display font-bold">
                                    {formatPrice(standardMonthly)}
                                  </span>
                                  <span className="text-muted-foreground">/month</span>
                                </div>
                                <div className="mt-2 flex flex-wrap justify-center gap-1.5">
                                  {monthsFreeLabel && (
                                    <Badge className="bg-green-600 hover:bg-green-600 text-white border-green-700">
                                      {monthsFreeLabel}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground mt-1.5">
                                  Billed annually ({formatPrice(plan.yearlyPriceUsd)}/year)
                                </p>
                              </div>
                            );
                          }

                          // Monthly view standard
                          return (
                            <div className="text-center mb-3">
                              <span className="text-5xl font-display font-bold">{formattedPrice}</span>
                              <span className="text-muted-foreground">/month</span>
                            </div>
                          );
                        })()}

                        {/* Per-listing value anchor */}
                        {perListing && (
                          <p className="text-center text-xs text-muted-foreground mb-5">
                            <span className="font-semibold text-foreground">{formatPrice(perListing)}</span> per listing
                          </p>
                        )}

                        {/* Capacity — the one headline limit */}
                        <div className={`rounded-xl border px-4 py-3.5 ${plan.popular ? 'border-primary/30 bg-primary/5' : 'border-border/60 bg-muted/30'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Building2 className={`w-5 h-5 ${plan.popular ? 'text-primary' : 'text-muted-foreground'}`} />
                              <span className="text-sm font-medium">Create listings</span>
                            </div>
                            <div className="flex items-baseline gap-1.5">
                              <span className={`text-2xl font-display font-bold ${plan.popular ? 'text-primary' : 'text-foreground'}`}>{plan.listings}</span>
                              <span className="text-sm text-muted-foreground">/mo</span>
                              {plan.multiplier && <span className="text-xs text-green-600 font-semibold ml-0.5">{plan.multiplier}</span>}
                            </div>
                          </div>
                          <div className="mt-3 pt-3 border-t border-border/50 grid grid-cols-3 text-center gap-1">
                            <div>
                              <div className="text-sm font-semibold">10</div>
                              <div className="text-[10px] text-muted-foreground leading-tight">photos each</div>
                            </div>
                            <div>
                              <div className="text-sm font-semibold">{REEDIT_LIMITS[plan.id]}</div>
                              <div className="text-[10px] text-muted-foreground leading-tight">re-edits / mo</div>
                            </div>
                            <div>
                              <div className="text-sm font-semibold">{seats}</div>
                              <div className="text-[10px] text-muted-foreground leading-tight">{seats === 1 ? 'team seat' : 'team seats'}</div>
                            </div>
                          </div>
                        </div>

                        {/* Optional per-listing add-ons */}
                        <div className="mt-4">
                          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-2">
                            Optional add-ons · per listing
                          </div>
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2 text-muted-foreground">
                                <ImagePlus className="w-4 h-4 shrink-0" /> Boost to 20 photos
                              </span>
                              <span className="font-semibold text-foreground">+{formatPrice(PER_LISTING_PHOTO_ADDON)}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2 text-muted-foreground">
                                <Video className="w-4 h-4 shrink-0" /> Cinematic AI video tour
                              </span>
                              <span className="font-semibold text-foreground">+{formatPrice(CINEMATIC_VIDEO_ADDON)}</span>
                            </div>
                          </div>
                        </div>

                        {/* What's included — value differentiators only */}
                        <div className="mt-5 pt-5 border-t border-border/50">
                          <p className="text-sm font-medium text-foreground mb-3">{plan.includesHeading}</p>
                          <ul className="space-y-2.5">
                            {plan.includes.map((item) => (
                              <li key={item} className="flex items-start gap-2.5 text-sm">
                                <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                                <span>{item}</span>
                              </li>
                            ))}
                            {plan.locked?.map((item) => (
                              <li key={item} className="flex items-start gap-2.5 text-sm text-muted-foreground/60">
                                <X className="w-4 h-4 shrink-0 mt-0.5" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </CardContent>

                      <CardFooter className="flex-col gap-2">
                        <Button
                          variant={isCurrent ? 'outline' : plan.ctaVariant}
                          className="w-full min-h-[48px] group"
                          onClick={() => handlePlanSelect(plan.id)}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : isCurrent ? (
                            'Current Plan'
                          ) : isSubscribed ? (
                            'Manage in Profile'
                          ) : (
                            <>
                              {plan.cta}
                              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                            </>
                          )}
                        </Button>
                        {isSubscribed ? (
                          <p className="text-xs text-muted-foreground text-center">
                            {currentTier === plan.id ? 'Your current plan' : 'Go to Profile → Plan to switch'}
                          </p>
                        ) : (
                          <p className="text-xs text-muted-foreground text-center">
                            Cancel anytime • Full toolkit included
                          </p>
                        )}
                      </CardFooter>
                    </Card>
                  </div>
                </Reveal>
              );
            })}

            {/* Enterprise — custom collaboration, contact sales only */}
            <Reveal delay={plans.length * 90} className="h-full">
              <div className="relative h-full">
                <Card className="relative flex flex-col h-full transition-all duration-300 border-border/60 card-premium">
                  <CardHeader className="text-center pb-4 pt-7">
                    <div className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-4 bg-primary/10">
                      <Briefcase className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Enterprise</CardTitle>
                    <CardDescription>Agencies with 10+ agents</CardDescription>
                  </CardHeader>

                  <CardContent className="flex-1">
                    <div className="text-center mb-3">
                      <span className="text-5xl font-display font-bold">Custom</span>
                    </div>
                    <p className="text-center text-xs text-muted-foreground mb-5">
                      Volume pricing tailored to your team
                    </p>

                    <div className="rounded-xl border px-4 py-3.5 border-border/60 bg-muted/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-muted-foreground" />
                          <span className="text-sm font-medium">Create listings</span>
                        </div>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-2xl font-display font-bold text-foreground">50+</span>
                          <span className="text-sm text-muted-foreground">/mo</span>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-border/50 grid grid-cols-3 text-center gap-1">
                        <div>
                          <div className="text-sm font-semibold">20</div>
                          <div className="text-[10px] text-muted-foreground leading-tight">photos each</div>
                        </div>
                        <div>
                          <div className="text-sm font-semibold">Custom</div>
                          <div className="text-[10px] text-muted-foreground leading-tight">re-edits / mo</div>
                        </div>
                        <div>
                          <div className="text-sm font-semibold">Unlimited</div>
                          <div className="text-[10px] text-muted-foreground leading-tight">team seats</div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 pt-5 border-t border-border/50">
                      <p className="text-sm font-medium text-foreground mb-3">Everything in Team, plus:</p>
                      <ul className="space-y-2.5">
                        {[
                          'Volume pricing for 50+ listings per month',
                          'Unlimited team seats & multi-office workspaces',
                          'Dedicated account manager & onboarding',
                          'Custom integrations & SSO on request',
                          'Priority SLA support',
                        ].map((item) => (
                          <li key={item} className="flex items-start gap-2.5 text-sm">
                            <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>

                  <CardFooter className="flex-col gap-2">
                    <Button asChild variant="default" className="w-full min-h-[48px] group">
                      <a
                        href="mailto:sales@listbright.ai?subject=Enterprise%20plan%20enquiry"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Contact sales
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                      </a>
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                      Tailored quote • Typically replies in 1 business day
                    </p>
                  </CardFooter>
                </Card>
              </div>
            </Reveal>
          </div>

          {/* Risk-reversal band */}
          <Reveal delay={120}>
            <div className="mt-12 rounded-2xl border border-border/60 bg-card/60 backdrop-blur p-6 sm:p-7">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                {guarantees.map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="w-[18px] h-[18px] text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{title}</div>
                      <div className="text-xs text-muted-foreground leading-snug">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── "The math" — value anchor ── */}
      <section className="py-16 sm:py-20 border-y border-border/40 bg-muted/30">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="outline" className="mb-4">The math</Badge>
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-3">
              One polished listing, <span className="text-gradient">a fraction of the cost</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              The old way means juggling a photographer, an editor, a copywriter and a web person — for every property.
              ListBright rolls it all into one.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 items-stretch">
            {/* Old way */}
            <Reveal>
              <div className="h-full rounded-2xl border border-border/60 bg-card p-7">
                <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-5">
                  The à la carte way
                </div>
                <ul className="space-y-3.5">
                  {oldWayCosts.map((item) => (
                    <li key={item.label} className="flex items-center justify-between text-sm border-b border-border/40 pb-3.5 last:border-0 last:pb-0">
                      <span className="text-muted-foreground line-through decoration-muted-foreground/30">{item.label}</span>
                      <span className="font-medium text-foreground/70">{item.cost}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t-2 border-dashed border-border/60 flex items-end justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground">Typical total, per listing</div>
                    <div className="text-xs text-muted-foreground">+ days of back-and-forth</div>
                  </div>
                  <div className="text-3xl font-display font-bold text-muted-foreground line-through decoration-2">$500+</div>
                </div>
              </div>
            </Reveal>

            {/* ListBright way */}
            <Reveal delay={100}>
              <div className="relative h-full rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-7 overflow-hidden">
                <div aria-hidden="true" className="absolute -top-10 -right-10 w-40 h-40 bg-primary/15 rounded-full blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-primary mb-5">
                    <Sparkles className="w-4 h-4" /> The ListBright way
                  </div>
                  <ul className="space-y-3.5">
                    {['AI-enhanced photos', 'Virtual staging & AI additions', 'Conversion-tested descriptions', 'Shareable microsite, flyer & video'].map((item) => (
                      <li key={item} className="flex items-center gap-2.5 text-sm">
                        <Check className="w-4 h-4 text-primary shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                    <li className="flex items-center gap-2.5 text-sm">
                      <Check className="w-4 h-4 text-primary shrink-0" />
                      <span>28-language translations</span>
                    </li>
                  </ul>
                  <div className="mt-6 pt-5 border-t-2 border-dashed border-primary/30 flex items-end justify-between">
                    <div>
                      <div className="text-xs text-muted-foreground">On the Growth plan</div>
                      <div className="text-xs text-primary font-medium">ready in ~10 minutes</div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-display font-bold text-primary">~$5</div>
                      <div className="text-xs text-muted-foreground">per listing</div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={150} className="text-center mt-10">
            <Button size="lg" variant="hero" className="group shadow-lg" onClick={scrollToPlans}>
              Start saving on every listing
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </Reveal>
        </div>
      </section>

      {/* ── Testimonials (dark social-proof band) ── */}
      <section className={`${DARK_SECTION} py-16 sm:py-24`}>
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(hsl(0_0%_100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0_0%_100%) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div aria-hidden="true" className="absolute top-0 right-1/4 w-[30rem] h-[30rem] bg-primary/15 rounded-full blur-3xl pointer-events-none" />
        <div className="container max-w-6xl mx-auto px-4 relative">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <Badge variant="secondary" className={`bg-primary/15 ${ACCENT} border-0 ring-1 ring-primary/25 px-4 py-1.5 mb-4`}>
              <Star className="w-3.5 h-3.5 mr-1.5 fill-current" /> Loved by agents
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-3">
              Agents close faster with ListBright
            </h2>
            <p className="text-white/65 text-lg">
              Realtors, brokers and agencies around the world ship sharper listings in minutes — not days.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={i * 90}>
                <figure className="h-full flex flex-col rounded-3xl ring-1 ring-white/10 bg-white/[0.04] p-6">
                  <Quote className={`w-7 h-7 ${ACCENT} mb-4`} />
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star key={s} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <blockquote className="flex-1 text-[0.95rem] leading-relaxed text-white/85 mb-6">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="flex items-center gap-3 pt-5 border-t border-white/10">
                    <img
                      src={t.photo}
                      alt={t.name}
                      loading="lazy"
                      className="w-11 h-11 rounded-full object-cover ring-1 ring-white/20"
                    />
                    <div>
                      <div className="text-sm font-semibold text-white">{t.name}</div>
                      <div className="text-xs text-white/55">{t.role} · {t.location}</div>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Single Property Kit — secondary option for one-off sellers ── */}
      <section className="py-16 sm:py-20 bg-gradient-to-r from-primary/5 to-primary/10 border-b border-border/50">
        <div className="container max-w-4xl mx-auto px-4">
          <Reveal className="text-center mb-8">
            <Badge variant="outline" className="mb-3">Just one property to list?</Badge>
            <h2 className="text-2xl sm:text-3xl font-display font-bold mb-2">Single Property Kit</h2>
            <p className="text-muted-foreground">Not a Realtor? Selling your own home? Pay once, no subscription — everything included.</p>
          </Reveal>
          <Reveal delay={80} className="relative">
            <div aria-hidden="true" className="absolute -inset-4 bg-gradient-to-tr from-primary/15 via-primary/5 to-transparent blur-3xl rounded-[2.5rem] opacity-70 pointer-events-none" />
            <Card className="relative border-2 border-primary/20 shadow-elevated">
              <CardHeader className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Package className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="text-2xl">{oneOffKit.name}</CardTitle>
                <CardDescription className="text-base">{oneOffKit.description}</CardDescription>
                <div className="text-5xl font-display font-bold mt-4">
                  {formatPrice(oneOffKit.priceUsd)}
                </div>
                <p className="text-sm text-muted-foreground">One-time payment • No subscription</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="font-bold text-primary text-lg">{oneOffKit.limits.properties}</div>
                    <div className="text-xs text-muted-foreground">property</div>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="font-bold text-primary text-lg">{oneOffKit.limits.photos10}</div>
                    <div className="text-xs text-muted-foreground">photos</div>
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-border/50 overflow-hidden max-w-md mx-auto">
                  <div className="p-3 bg-muted/50 space-y-1">
                    <div className="flex items-center gap-2">
                      <ImagePlus className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">10 photos per property</span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-6">Upgrade to 20 for {formatPrice(oneOffKit.extraPhotosAddon)}/listing</span>
                  </div>
                  <div className="px-3 py-2.5 bg-green-50 dark:bg-green-950/20 border-t border-green-200/50 dark:border-green-800/30 flex items-center gap-2">
                    <RotateCcw className="w-3.5 h-3.5 text-green-600 shrink-0" />
                    <span className="text-xs font-medium text-green-700 dark:text-green-400">Includes {REEDIT_LIMITS.one_off_kit} re-edits for this property</span>
                  </div>
                </div>

                <ul className="mt-4 space-y-2 max-w-md mx-auto">
                  {oneOffKit.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500 shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="flex-col gap-2 justify-center">
                <Button
                  size="lg"
                  variant="hero"
                  className="group"
                  onClick={handleTryItKit}
                  disabled={loadingPlan === 'try_it_kit'}
                >
                  {loadingPlan === 'try_it_kit' ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Get the Single Property Kit — {formatPrice(oneOffKit.priceUsd)}
                      <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => navigate('/single-property-kit')}
                  className="text-muted-foreground"
                >
                  Learn more about the Kit
                </Button>
              </CardFooter>
            </Card>
          </Reveal>
        </div>
      </section>

      {/* ── Why Upgrade Section ── */}
      <section className="py-16 sm:py-20">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">Why teams pick Growth & Team</h2>
            <p className="text-muted-foreground text-lg">The features that move the needle when you're listing every week.</p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {upgradeReasons.map((tier, i) => {
              const Icon = tier.icon;
              const isGrowth = tier.tier === 'Growth';
              return (
                <Reveal key={tier.tier} delay={i * 100}>
                  <Card className={`relative h-full ${isGrowth ? 'border-primary shadow-lg' : 'border-border/50 card-premium'}`}>
                    {isGrowth && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-primary text-primary-foreground">Best Value</Badge>
                      </div>
                    )}
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isGrowth ? 'gradient-gold text-primary-foreground' : 'bg-primary/10'}`}>
                          <Icon className={`w-5 h-5 ${isGrowth ? '' : 'text-primary'}`} />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{tier.tier}</CardTitle>
                          <CardDescription>{tier.headline}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {tier.reasons.map((reason, idx) => (
                          <li key={idx} className="flex items-start gap-2.5 text-sm">
                            <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                            <span>{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Comparison Table ── */}
      <section className="py-16 sm:py-20 border-t border-border/40 bg-muted/20">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">Compare every plan</h2>
            <p className="text-muted-foreground text-lg">Everything side by side — pick the cap that fits how you list.</p>
            <p className="text-xs text-muted-foreground mt-3 sm:hidden">← Scroll to compare →</p>
          </Reveal>

          <Reveal delay={80}>
            <div className="overflow-x-auto -mx-4 px-4 scrollbar-none">
              <div className="min-w-[560px] rounded-2xl border border-border/60 overflow-hidden shadow-soft">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-muted/40">
                      <th className="text-left py-4 px-4 font-semibold text-xs sm:text-sm sticky left-0 bg-muted/40 z-10 min-w-[150px]">Feature</th>
                      <th className="text-center py-4 px-3 font-semibold text-xs sm:text-sm min-w-[90px]">Starter</th>
                      <th className="text-center py-4 px-3 font-semibold text-xs sm:text-sm bg-primary text-primary-foreground min-w-[90px]">
                        Growth
                        <span className="block text-[10px] font-normal opacity-90">Most popular</span>
                      </th>
                      <th className="text-center py-4 px-3 font-semibold text-xs sm:text-sm min-w-[90px]">Team</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonFeatures.map((row, i) => (
                      <tr key={row.name} className={`border-t border-border/40 ${i % 2 === 0 ? 'bg-card' : 'bg-muted/20'}`}>
                        <td className={`py-3 px-4 text-xs sm:text-sm sticky left-0 bg-inherit z-10 ${row.isPrice ? 'font-bold' : 'font-medium'}`}>{row.name}</td>
                        <td className={`py-3 px-3 text-center text-xs sm:text-sm ${row.isPrice ? 'font-semibold' : ''}`}>
                          <CompareCell value={row.starter} />
                        </td>
                        <td className={`py-3 px-3 text-center text-xs sm:text-sm bg-primary/5 ${row.isPrice ? 'font-semibold' : ''}`}>
                          <CompareCell value={row.growth} />
                        </td>
                        <td className={`py-3 px-3 text-center text-xs sm:text-sm ${row.isPrice ? 'font-semibold' : ''}`}>
                          <CompareCell value={row.studio} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120} className="text-center mt-10">
            <Button size="lg" variant="hero" className="group shadow-lg" onClick={scrollToPlans}>
              Choose your plan
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </Reveal>
        </div>
      </section>

      {/* ── What Each Feature Does ── */}
      <section className="py-16 sm:py-20">
        <div className="container max-w-5xl mx-auto px-4">
          <Reveal className="text-center mb-12">
            <Badge variant="outline" className="mb-4"><Sparkles className="w-3.5 h-3.5 mr-1.5 text-primary" /> What's included</Badge>
            <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">Every plan ships the full toolkit</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">No upsells on the essentials — these come standard with every property you create.</p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {featureExplanations.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <Reveal key={feature.id} delay={(i % 2) * 80}>
                  <Card className="h-full border border-border/60 card-premium">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{feature.name}</CardTitle>
                          <Badge variant="outline" className="text-xs mt-1">{feature.pricing}</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">{feature.description}</p>
                      <ul className="space-y-1">
                        {feature.includes.map((item, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500 shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                      {feature.note && (
                        <p className="text-xs text-muted-foreground mt-3 p-2 bg-muted rounded">
                          💡 {feature.note}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FAQ Section (two-column, sticky) ── */}
      <section className="py-16 sm:py-20 border-t border-border/40 bg-muted/30">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16">
            <Reveal className="lg:sticky lg:top-24 self-start space-y-5">
              <Badge variant="outline"><Sparkles className="w-3.5 h-3.5 mr-1.5 text-primary" /> FAQ</Badge>
              <h2 className="text-3xl md:text-4xl font-display font-bold leading-tight">
                Questions, answered
              </h2>
              <p className="text-muted-foreground">
                Everything you need to know about plans, the Kit, and how it all works.
                Still stuck?{' '}
                <button onClick={() => navigate('/contact')} className="text-primary font-medium hover:underline">Talk to us.</button>
              </p>
            </Reveal>
            <Reveal delay={100}>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left text-base font-semibold hover:no-underline min-h-[48px]">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-16 sm:py-24">
        <div className="container max-w-3xl mx-auto px-4">
          <Reveal className="relative text-center space-y-6 p-10 sm:p-14 rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 overflow-hidden">
            <div aria-hidden="true" className="absolute top-1/2 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 pointer-events-none" />
            <div aria-hidden="true" className="absolute top-1/2 right-1/4 w-64 h-64 bg-accent/10 rounded-full blur-3xl -translate-y-1/2 pointer-events-none" />
            <div className="relative space-y-6">
              <Badge variant="secondary" className="px-4 py-2">🎉 Ready in minutes</Badge>
              <h2 className="text-3xl sm:text-4xl font-display font-bold">
                Your next listing, ready in minutes
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Sellers, Realtors, and agents in 15+ countries trust ListBright to turn raw photos into a polished,
                ready-to-publish listing — without the 4-hour copy-paste marathon.
              </p>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-primary" /> Start free, no card</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-primary" /> All features included</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-primary" /> Cancel anytime</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-1">
                <Button size="xl" variant="hero" className="group min-h-[52px] w-full sm:w-auto" onClick={() => navigate('/auth')}>
                  Create Your Listing
                  <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button size="lg" variant="outline" className="min-h-[48px] w-full sm:w-auto" onClick={() => navigate('/contact')}>
                  Talk to Sales
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
